import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Label, TextInput, Card, Alert, Textarea, Select, Spinner } from 'flowbite-react';
import FeatureTags from '../components/FeatureTags';
import { api } from '../api';
import { useToast } from '../components/ToastProvider';

const CATEGORY_OPTIONS = {
  residential: [
    { value: 'apartment', label: 'Квартира' },
    { value: 'room', label: 'Комната' },
  ],
  country: [
    { value: 'house', label: 'Дом' },
    { value: 'cottage', label: 'Коттедж' },
    { value: 'townhouse', label: 'Таунхаус' },
    { value: 'land-plot', label: 'Земельный участок' },
  ],
  commercial: [
    { value: 'office', label: 'Офис' },
    { value: 'retail', label: 'Ритейл' },
    { value: 'warehouse', label: 'Склад' },
    { value: 'free-purpose', label: 'Свободного назначения' },
  ],
};

export default function PropertyForm() {
  const { appUuid, propId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const isEdit = Boolean(propId);

  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageObjs, setImageObjs] = useState([]);

  const [form, setForm] = useState({
    application_id: '',
    type: 'apartment',
    title: '',
    description: '',
    price: '',
    currency: 'RUB',
    area: '',
    rooms: '',
    floor: '',
    total_floors: '',
    land_area: '',
    address: '',
    city: '',
    district: '',
    lat: '',
    lng: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    images: '',
    features: '',
    // extended
    deal_type: 'sale',
    property_type_ext: 'residential',
    category_ext: 'apartment',
    building_name: '',
    ceiling_height: '',
    purpose: '',
    parking: '',
    developer: '',
    house_material: '',
    readiness: '',
    mortgage: false,
    cadastral_number: '',
    built_year: '',
    new_building: false,
    // specialized (YRL)
    living_space: '',
    kitchen_space: '',
    room_space: '',
    bathroom_unit: '',
    renovation: '',
    balcony: '',
    phone: false,
    internet: false,
    lift: false,
    gas_supply: false,
    water_supply: false,
    electricity_supply: false,
    heating_supply: false,
    sewage_supply: false,
    security: '',
    entrance: '',
    layout: '',
    parking_places: '',
    building_section: '',
    building_id: '',
    yandex_building_id: '',
    office_class: '',
    ready_quarter: '',
  });

  useEffect(() => {
    if (isEdit) {
      (async () => {
        try {
          const { item } = await api.get(`/properties/${propId}`);
          setImageObjs(item.images || []);
          setForm({
            application_id: item.application_id || '',
            type: item.type || 'apartment',
            title: item.title || '',
            description: item.description || '',
            price: item.price ?? '',
            currency: item.currency || 'RUB',
            area: item.area ?? '',
            rooms: item.rooms ?? '',
            floor: item.floor ?? '',
            total_floors: item.total_floors ?? '',
            land_area: item.land_area ?? '',
            address: item.address || '',
            city: item.city || '',
            district: item.district || '',
            lat: item.lat ?? '',
            lng: item.lng ?? '',
            contact_name: item.contact_name || '',
            contact_phone: item.contact_phone || '',
            contact_email: item.contact_email || '',
            images: (item.images || []).map(x => x.url).join(', '),
            features: (item.features || []).join(', '),
            // extended
            deal_type: item.deal_type || 'sale',
            property_type_ext: item.property_type_ext || 'residential',
            category_ext: item.category_ext || 'apartment',
            building_name: item.building_name || '',
            ceiling_height: item.ceiling_height ?? '',
            purpose: item.purpose || '',
            parking: item.parking || '',
            developer: item.developer || '',
            house_material: item.house_material || '',
            readiness: item.readiness || '',
            mortgage: !!item.mortgage,
            cadastral_number: item.cadastral_number || '',
            built_year: item.built_year ?? '',
            new_building: !!item.new_building,
            // specialized
            living_space: item.living_space ?? '',
            kitchen_space: item.kitchen_space ?? '',
            room_space: item.room_space ?? '',
            bathroom_unit: item.bathroom_unit || '',
            renovation: item.renovation || '',
            balcony: item.balcony || '',
            phone: !!item.phone,
            internet: !!item.internet,
            lift: !!item.lift,
            gas_supply: !!item.gas_supply,
            water_supply: !!item.water_supply,
            electricity_supply: !!item.electricity_supply,
            heating_supply: !!item.heating_supply,
            sewage_supply: !!item.sewage_supply,
            security: item.security || '',
            entrance: item.entrance || '',
            layout: item.layout || '',
            parking_places: item.parking_places ?? '',
            building_section: item.building_section || '',
            building_id: item.building_id || '',
            yandex_building_id: item.yandex_building_id || '',
            office_class: item.office_class || '',
            ready_quarter: item.ready_quarter || '',
          });
        } catch (e) {
          setError(e.message);
        }
      })();
    }
  }, [isEdit, propId]);

  const validateForm = () => {
    if (!form.deal_type) return 'Укажите тип сделки';
    if (!form.property_type_ext) return 'Укажите тип недвижимости';
    if (!form.category_ext) return 'Укажите категорию';
    if (!form.title) return 'Укажите заголовок';
    if (form.price === '' || form.price == null || Number.isNaN(Number(form.price))) return 'Укажите корректную цену';
    if (!form.currency) return 'Укажите валюту';
    if (!form.city && !form.address) return 'Укажите город или адрес';
    if (form.property_type_ext === 'commercial' && !form.purpose) return 'Для коммерции укажите назначение';
    if (form.new_building && !form.developer) return 'Для новостроек укажите застройщика';
    if (form.new_building && !form.readiness) return 'Для новостроек укажите состояние строительства';
    return '';
  };

  const submit = async () => {
    try {
      const ve = validateForm();
      if (ve) { setValidationError(ve); return; }
      setBusy(true);
      const payload = {
        ...form,
        application_id: appUuid || form.application_id || undefined,
        price: form.price === '' ? undefined : Number(form.price),
        area: form.area === '' ? undefined : Number(form.area),
        rooms: form.rooms === '' ? undefined : Number(form.rooms),
        floor: form.floor === '' ? undefined : Number(form.floor),
        total_floors: form.total_floors === '' ? undefined : Number(form.total_floors),
        land_area: form.land_area === '' ? undefined : Number(form.land_area),
        lat: form.lat === '' ? undefined : Number(form.lat),
        lng: form.lng === '' ? undefined : Number(form.lng),
        ceiling_height: form.ceiling_height === '' ? undefined : Number(form.ceiling_height),
        built_year: form.built_year === '' ? undefined : Number(form.built_year),
        living_space: form.living_space === '' ? undefined : Number(form.living_space),
        kitchen_space: form.kitchen_space === '' ? undefined : Number(form.kitchen_space),
        room_space: form.room_space === '' ? undefined : Number(form.room_space),
        parking_places: form.parking_places === '' ? undefined : Number(form.parking_places),
        images: form.images ? form.images.split(',').map(s => ({ url: s.trim() })).filter(x => x.url) : undefined,
        features: form.features ? form.features.split(',').map(s => ({ name: s.trim() })).filter(x => x.name) : undefined,
      };
      if (isEdit) {
        await api.put(`/properties/${propId}`, payload);
        addToast({ type: 'success', message: 'Объект сохранен' });
      } else {
        await api.post('/properties', payload);
        addToast({ type: 'success', message: 'Объект создан' });
      }
      navigate(`/${appUuid}/properties`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const currentCategories = CATEGORY_OPTIONS[form.property_type_ext] || [];
  const isResidential = form.property_type_ext === 'residential';
  const isCountry = form.property_type_ext === 'country';
  const isCommercial = form.property_type_ext === 'commercial';
  const isLandPlot = form.category_ext === 'land-plot';
  const isNewBuilding = !!form.new_building;

  const showRooms = isResidential && form.category_ext !== 'room';
  const showRoomSpace = isResidential && form.category_ext === 'room';
  const showFloors = (isResidential || isCommercial) && !isLandPlot;
  const showLandArea = isCountry || isLandPlot;
  const showBuildingName = (isResidential || isCommercial) && !isLandPlot;
  const showHouseMaterial = !isLandPlot;
  const showBuiltYear = (isResidential || isCommercial || (isCountry && !isLandPlot));
  const showCeilingHeight = (isResidential || isCommercial) && !isLandPlot;
  const showReadiness = isNewBuilding;
  const showDeveloper = isNewBuilding;
  const showCadastral = isCountry || isLandPlot;

  const uploadImage = async (file) => {
    if (!isEdit) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/properties/${propId}/images/upload-file`, fd);
      const { item } = await api.get(`/properties/${propId}`);
      setImageObjs(item.images || []);
      setForm(f => ({ ...f, images: (item.images || []).map(x => x.url).join(', ') }));
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!isEdit) return;
    try {
      await api.delete(`/properties/${propId}/images/${imageId}`);
      const { item } = await api.get(`/properties/${propId}`);
      setImageObjs(item.images || []);
      setForm(f => ({ ...f, images: (item.images || []).map(x => x.url).join(', ') }));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="grid gap-4">
      <Card>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? 'Редактировать объект' : 'Создать объект'}</h2>
          <div className="flex flex-wrap gap-2">
            <Button color="light" onClick={() => navigate(`/${appUuid}/properties`)}>Назад к списку</Button>
            <Button onClick={submit} disabled={busy || uploading}>{busy ? (<span className="inline-flex items-center gap-2"><Spinner size="sm" /> Сохранение...</span>) : (isEdit ? 'Сохранить' : 'Создать')}</Button>
          </div>
        </div>
        {error && <Alert color="failure" className="mt-2">{error}</Alert>}
        {validationError && <Alert color="failure" className="mt-2">{validationError}</Alert>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="md:col-span-2 text-sm font-medium text-gray-600">1) Основная информация</div>
          <div>
            <Label value="Тип сделки" />
            <Select value={form.deal_type} onChange={e => setForm(f => ({ ...f, deal_type: e.target.value }))}>
              <option value="sale">Продажа</option>
              <option value="rent">Аренда</option>
            </Select>
          </div>
          <div>
            <Label value="Тип недвижимости" />
            <Select value={form.property_type_ext} onChange={e => setForm(f => ({ ...f, property_type_ext: e.target.value, category_ext: (CATEGORY_OPTIONS[e.target.value] || [])[0]?.value || '' }))}>
              <option value="residential">Жилая</option>
              <option value="country">Загородная</option>
              <option value="commercial">Коммерческая</option>
            </Select>
          </div>
          <div>
            <Label value="Категория" />
            <Select value={form.category_ext} onChange={e => setForm(f => ({ ...f, category_ext: e.target.value }))}>
              {currentCategories.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label value="Заголовок" />
            <TextInput value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Например: 2-комн. апартаменты в Дубае" />
          </div>
          <div className="md:col-span-2">
            <Label value="Описание" />
            <Textarea rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Краткое описание объекта, преимущества и т.п." />
          </div>

          <div className="md:col-span-2 text-sm font-medium text-gray-600 mt-2">2) Параметры и площади</div>
          <div>
            <Label value="Цена" />
            <TextInput type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
          </div>
          <div>
            <Label value="Валюта" />
            <TextInput value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} />
          </div>
          <div>
            <Label value="Общая площадь (м²)" />
            <TextInput type="number" value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} />
          </div>
          {showRooms && (
            <div>
              <Label value="Комнат" />
              <TextInput type="number" value={form.rooms} onChange={e => setForm(f => ({ ...f, rooms: e.target.value }))} />
            </div>
          )}
          {showRoomSpace && (
            <div>
              <Label value="Площадь комнаты (м²)" />
              <TextInput type="number" value={form.room_space} onChange={e => setForm(f => ({ ...f, room_space: e.target.value }))} />
            </div>
          )}
          {showFloors && (
            <>
              <div>
                <Label value="Этаж" />
                <TextInput type="number" value={form.floor} onChange={e => setForm(f => ({ ...f, floor: e.target.value }))} />
              </div>
              <div>
                <Label value="Этажность дома" />
                <TextInput type="number" value={form.total_floors} onChange={e => setForm(f => ({ ...f, total_floors: e.target.value }))} />
              </div>
            </>
          )}
          {showLandArea && (
            <div>
              <Label value="Площадь участка (м²)" />
              <TextInput type="number" value={form.land_area} onChange={e => setForm(f => ({ ...f, land_area: e.target.value }))} />
            </div>
          )}

          {isResidential && (
            <>
              <div>
                <Label value="Жилая площадь (м²)" />
                <TextInput type="number" value={form.living_space} onChange={e => setForm(f => ({ ...f, living_space: e.target.value }))} />
              </div>
              <div>
                <Label value="Кухня (м²)" />
                <TextInput type="number" value={form.kitchen_space} onChange={e => setForm(f => ({ ...f, kitchen_space: e.target.value }))} />
              </div>
              <div>
                <Label value="Санузел" />
                <TextInput value={form.bathroom_unit} onChange={e => setForm(f => ({ ...f, bathroom_unit: e.target.value }))} placeholder="раздельный|совмещенный" />
              </div>
              <div>
                <Label value="Ремонт" />
                <TextInput value={form.renovation} onChange={e => setForm(f => ({ ...f, renovation: e.target.value }))} placeholder="евро|косметический|без ремонта" />
              </div>
              <div>
                <Label value="Балкон" />
                <TextInput value={form.balcony} onChange={e => setForm(f => ({ ...f, balcony: e.target.value }))} placeholder="есть|нет|лоджия" />
              </div>
              <div className="flex items-center gap-4 mt-2">
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.checked }))} /> Телефон</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.internet} onChange={e => setForm(f => ({ ...f, internet: e.target.checked }))} /> Интернет</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.lift} onChange={e => setForm(f => ({ ...f, lift: e.target.checked }))} /> Лифт</label>
              </div>
            </>
          )}

          <div className="md:col-span-2 text-sm font-medium text-gray-600 mt-2">3) Коммуникации и коммерческие параметры</div>
          {isCountry && (
            <>
              <div className="flex flex-wrap gap-4 mt-2">
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.gas_supply} onChange={e => setForm(f => ({ ...f, gas_supply: e.target.checked }))} /> Газ</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.water_supply} onChange={e => setForm(f => ({ ...f, water_supply: e.target.checked }))} /> Вода</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.electricity_supply} onChange={e => setForm(f => ({ ...f, electricity_supply: e.target.checked }))} /> Электричество</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.heating_supply} onChange={e => setForm(f => ({ ...f, heating_supply: e.target.checked }))} /> Отопление</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!form.sewage_supply} onChange={e => setForm(f => ({ ...f, sewage_supply: e.target.checked }))} /> Канализация</label>
              </div>
            </>
          )}

          {isCommercial && (
            <>
              <div>
                <Label value="Назначение" />
                <TextInput value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="office|retail|warehouse|..." />
              </div>
              <div>
                <Label value="Класс здания" />
                <TextInput value={form.office_class} onChange={e => setForm(f => ({ ...f, office_class: e.target.value }))} placeholder="A+|A|B|C" />
              </div>
              <div>
                <Label value="Планировка" />
                <TextInput value={form.layout} onChange={e => setForm(f => ({ ...f, layout: e.target.value }))} placeholder="open-space|cabinet" />
              </div>
              <div>
                <Label value="Вход" />
                <TextInput value={form.entrance} onChange={e => setForm(f => ({ ...f, entrance: e.target.value }))} placeholder="с улицы|со двора|общий" />
              </div>
              <div>
                <Label value="Охрана" />
                <TextInput value={form.security} onChange={e => setForm(f => ({ ...f, security: e.target.value }))} />
              </div>
              <div>
                <Label value="Парковка" />
                <TextInput value={form.parking} onChange={e => setForm(f => ({ ...f, parking: e.target.value }))} placeholder="underground|ground|multi-level" />
              </div>
              <div>
                <Label value="Мест на парковке" />
                <TextInput type="number" value={form.parking_places} onChange={e => setForm(f => ({ ...f, parking_places: e.target.value }))} />
              </div>
              <div>
                <Label value="Секция здания" />
                <TextInput value={form.building_section} onChange={e => setForm(f => ({ ...f, building_section: e.target.value }))} />
              </div>
              <div>
                <Label value="ID здания" />
                <TextInput value={form.building_id} onChange={e => setForm(f => ({ ...f, building_id: e.target.value }))} />
              </div>
              <div>
                <Label value="Yandex building ID" />
                <TextInput value={form.yandex_building_id} onChange={e => setForm(f => ({ ...f, yandex_building_id: e.target.value }))} />
              </div>
              <div>
                <Label value="Срок сдачи (квартал)" />
                <TextInput value={form.ready_quarter} onChange={e => setForm(f => ({ ...f, ready_quarter: e.target.value }))} placeholder="2025-Q3" />
              </div>
            </>
          )}

          <div className="md:col-span-2 text-sm font-medium text-gray-600 mt-2">4) Локация и контакты</div>
          <div className="md:col-span-2">
            <Label value="Адрес" />
            <TextInput value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
          </div>
          <div>
            <Label value="Город" />
            <TextInput value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          </div>
          <div>
            <Label value="Район" />
            <TextInput value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))} />
          </div>
          <div>
            <Label value="Широта" />
            <TextInput type="number" value={form.lat} onChange={e => setForm(f => ({ ...f, lat: e.target.value }))} />
          </div>
          <div>
            <Label value="Долгота" />
            <TextInput type="number" value={form.lng} onChange={e => setForm(f => ({ ...f, lng: e.target.value }))} />
          </div>
          <div>
            <Label value="Контакт: имя" />
            <TextInput value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} />
          </div>
          <div>
            <Label value="Контакт: телефон" />
            <TextInput value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="+7 999 123-45-67" />
          </div>
          <div>
            <Label value="Контакт: email" />
            <TextInput type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="sales@example.com" />
          </div>

          {showBuildingName && (
            <div>
              <Label value="Название ЖК/здания" />
              <TextInput value={form.building_name} onChange={e => setForm(f => ({ ...f, building_name: e.target.value }))} />
            </div>
          )}
          {showHouseMaterial && (
            <div>
              <Label value="Материал дома" />
              <TextInput value={form.house_material} onChange={e => setForm(f => ({ ...f, house_material: e.target.value }))} placeholder="монолит|кирпич|панель|блок" />
            </div>
          )}
          {showBuiltYear && (
            <div>
              <Label value="Год постройки" />
              <TextInput type="number" value={form.built_year} onChange={e => setForm(f => ({ ...f, built_year: e.target.value }))} />
            </div>
          )}
          {showCeilingHeight && (
            <div>
              <Label value="Высота потолков (м)" />
              <TextInput type="number" step="0.01" value={form.ceiling_height} onChange={e => setForm(f => ({ ...f, ceiling_height: e.target.value }))} />
            </div>
          )}
          {showReadiness && (
            <div>
              <Label value="Состояние строительства" />
              <TextInput value={form.readiness} onChange={e => setForm(f => ({ ...f, readiness: e.target.value }))} placeholder="ready|under-construction" />
            </div>
          )}
          {showDeveloper && (
            <div>
              <Label value="Застройщик" />
              <TextInput value={form.developer} onChange={e => setForm(f => ({ ...f, developer: e.target.value }))} />
            </div>
          )}

          <div className="flex items-center gap-2 mt-6">
            <input id="mortgage" type="checkbox" checked={!!form.mortgage} onChange={e => setForm(f => ({ ...f, mortgage: e.target.checked }))} />
            <Label htmlFor="mortgage" value="Ипотека" />
          </div>
          {showCadastral && (
            <div>
              <Label value="Кадастровый номер" />
              <TextInput value={form.cadastral_number} onChange={e => setForm(f => ({ ...f, cadastral_number: e.target.value }))} />
            </div>
          )}
          <div className="flex items-center gap-2 mt-6">
            <input id="new_building" type="checkbox" checked={!!form.new_building} onChange={e => setForm(f => ({ ...f, new_building: e.target.checked }))} />
            <Label htmlFor="new_building" value="Новостройка" />
          </div>

          <div className="md:col-span-2 text-sm font-medium text-gray-600 mt-2">5) Медиа и особенности</div>
          <div className="md:col-span-2">
            <Label value="Картинки (URLs через запятую)" />
            <Textarea rows={2} value={form.images} onChange={e => setForm(f => ({ ...f, images: e.target.value }))} placeholder="https://... , https://..." />
          </div>
          <div className="md:col-span-2">
            <Label value="Особенности (теги)" />
            <FeatureTags
              appUuid={appUuid}
              value={(form.features || '').split(',').map(s => s.trim()).filter(Boolean)}
              onChange={(arr) => setForm(f => ({ ...f, features: arr.join(', ') }))}
              placeholder="начните вводить, чтобы выбрать из подсказок; Enter/Tab/запятая — добавить"
            />
          </div>

          {isEdit && (
            <div className="md:col-span-2 grid gap-2">
              <Label value="Фотографии (S3)" />
              <div className="flex flex-wrap gap-2">
                {imageObjs.map(img => (
                  <div key={img.id} className="w-24">
                    <img src={img.url} alt="img" className="w-24 h-24 object-cover rounded" />
                    <Button color="failure" size="xs" className="mt-1 w-full" onClick={() => deleteImage(img.id)}>Удалить</Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <input type="file" accept="image/*" onChange={(e) => e.target.files && e.target.files[0] && uploadImage(e.target.files[0])} />
                {uploading && <Spinner />}
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button onClick={submit} disabled={busy || uploading}>{busy ? (<span className="inline-flex items-center gap-2"><Spinner size="sm" /> Сохранение...</span>) : (isEdit ? 'Сохранить' : 'Создать')}</Button>
          <Button color="light" onClick={() => navigate(`/${appUuid}/properties`)}>Отмена</Button>
        </div>
        {isEdit && imageObjs.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {imageObjs.map(im => (
              <div key={im.id} className="relative border rounded">
                <img src={im.url} alt="" className="w-full h-32 object-cover" />
                <Button color="failure" size="xs" className="absolute top-1 right-1" onClick={() => deleteImage(im.id)}>×</Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}